import { Button, Container, Grid, Input, styled, Typography } from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColumns, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid'
import { ConfirmDialog, DataGridStyles, Icon, Modal, SnackBarContext } from 'components'
import { DatagridPlaceholder } from 'components/datagridPlaceholder'
import { format } from 'date-fns'
import { inject, observer } from 'mobx-react'
import { Company } from 'models'
import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Pages } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import { ICompany } from 'types'
import { CompanyForm } from 'views/settings/companyForm'

export interface ICustomersProps {
  authStore?:    AuthStore,
  routingStore?: RoutingStore,
}

/**
 * Displays an overview list of all customers in the system for admins
 *
 * @type Admin
 */

export const CustomerList = inject('authStore', 'routingStore')(observer((props: ICustomersProps) => {
  const {routingStore } = props;
  const { toast } = React.useContext(SnackBarContext);
  const { t } = useTranslation()

  const [companies,    setCompanies]    = React.useState<Company[]>([]);;
  const [company,      setCompany]      = React.useState<Company>();
  const [confirmValue, setConfirmValue] = React.useState<string>("");
  const [openModal,    setOpenModal]    = React.useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState<boolean>(false);

  React.useEffect(() => {
    Company.get().then(async (c: Company[]) => {
      setCompanies(c);
      for(const _c of c) {
        await _c.loadUserCount();
      }
      setCompanies([...c]);
    });
  }, []);

  const columns: GridColumns = [
    {
      flex:            2,
      field:           'title',
      headerName:      t('name'),
      headerClassName: 'columnHeader',
    },{
      flex:            1,
      field:           'user_count',
      headerName:      t('user_plural'),
      headerClassName: 'columnHeader',
      filterable:      false,
    },{
      flex:            1,
      field:           'created_at',
      headerName:      t('created_at'),
      headerClassName: 'columnHeader',
      filterable:      false,
      renderCell:      (params: GridRenderCellParams<string, RowData>) => (
        format(new Date(params.value), 'yyyy-MM-dd')
      ),
    },{
      field:      'action',
      type:       'actions',
      filterable: false,
      getActions: (params: GridRowParams<Company>) => [
        <GridActionsCellItem onClick={() => {setOpenConfirmDialog(true), setCompany(params.row)}} icon={<StyledIcon size='size-18' iconId="bin" />} showInMenu={false} label={t('delete')} />,
      ],
    },
  ];

  return (
      <>
      <Container maxWidth='xl'>
      <Grid pt={3} container>
        <Grid
          item
          xs             = {12}
          mb             = {3}
          display        = 'flex'
          justifyContent = 'space-between'
          alignItems     = 'center'
        >
          <Typography variant='h2'>{t('clients')}</Typography>
          <Button
            variant   = 'contained'
            startIcon = {<Icon iconId='add' />}
            onClick   = {() => {
              setCompany(
                Company.create({
                  title: '',
                  sparta_id: undefined,
                })
              )
              setOpenModal(true)
            } }
          >
            {t('add_client')}
          </Button>
        </Grid>
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Typography mb={3} variant='h3'>
            {t('add_client')}z
          </Typography>
          <CompanyForm
            required
            dontSave
            hasConfirmationButtons
            company  = {company}
            onCancel = {() => setOpenModal(false)}
            onSave   = {async () => {
              if (await company.save()) {
                setCompanies([...companies, company])
                setOpenModal(false)
              }
              else {
                console.error("Failed")
                toast(t('toast_error_add_client'), 'error')
              }
            } } />
        </Modal>
        <Grid xs={12} mb={3} item>
          <Typography variant='body1'>
            {t('client_page_description')}
          </Typography>
        </Grid>
      </Grid>
      <DataGridStyles>
        <DataGrid
          autoHeight
          disableSelectionOnClick
          disableColumnSelector
          rowHeight  = {66}
          density    = 'standard'
          columns    = {columns}
          pageSize   = {25}
          rows       = {companies}
          components = {{
            NoRowsOverlay: () =>
            (<DatagridPlaceholder title={t('datagrid_placeholder_company_list')}/>)
          }}
          rowsPerPageOptions   = {[25]}
          hideFooterPagination = {Object.values(companies).length < 25}
          onRowClick           = {(params: GridRowParams<RowData>) => routingStore.push(Pages.Customers, { id: "" + params.row.id, view: 'users' })} />
      </DataGridStyles>
    </Container>
      <ConfirmDialog
        content      = {
          <Trans 
            t          = {t}
            values     = {{company: company?.title}}
            i18nKey    = 'delete_customer_confirm_details'
            components = {{bold: <strong />}}
          />
        }
        disabled          = {confirmValue !== company?.title} 
        confirmInput      = {<Input value={confirmValue} onChange={(e) => setConfirmValue(e.target.value)} />}
        open              = {openConfirmDialog}
        onClose           = {() => setOpenConfirmDialog(false)}
        title             = {t('confirm_delete_customer_question') + '?'}
        confirmButtonText = {t('delete')}
        cancelButtonText  = {t('cancel')}
        onCancel          = {() => {setOpenConfirmDialog(false); setConfirmValue("")}}
        onConfirm         = {async () => {
          if (await company.remove()) {
            setCompanies(companies.filter((c) => company.id !== c.id))
          } else {
            toast(t('toast_error_remove_company'), 'error')
          }
          setOpenConfirmDialog(false)
          setConfirmValue("")
        } } 
      />
    </>
  );
}));

type RowData = ICompany & {user_count?: number};


const StyledIcon = styled(Icon)`
  svg {
    color: black;
  }
`;